"use client";
import { Button, Input, Label } from "@repo/design-system";
import { useEffect, useRef, useState } from "react";
import { signIn, signInWithOAuth } from "../client";
import { passwordRejectionMessage, toPasswordRejectionCode } from "../lib/password-rejection";
import { parseRedirectTo } from "../lib/redirect";
import type { AuthCopy, AuthErrorCopy } from "./auth-copy";
import { DEFAULT_AUTH_COPY } from "./auth-copy";
import { AuthSplitLayout } from "./auth-split-layout";
import type { PasswordRequirement } from "./password-strength";
import { PasswordStrength } from "./password-strength";
import { TurnstileWidget, type TurnstileWidgetRef } from "./turnstile-widget";

// Exported for the partner sign-up wizard (one-arg call). `copy` is optional:
// the auth-form passes localized labels; callers without copy get English.
export const getPasswordRequirements = (password: string, copy?: AuthCopy): PasswordRequirement[] => [
  { label: copy?.reqMinChars ?? "At least 8 characters", met: /.{8,}/.test(password) },
  { label: copy?.reqUppercase ?? "Contains uppercase letter", met: /[A-Z]/.test(password) },
  { label: copy?.reqLowercase ?? "Contains lowercase letter", met: /[a-z]/.test(password) },
  { label: copy?.reqNumber ?? "Contains number", met: /[0-9]/.test(password) },
];

const getErrorMessage = (error: unknown, errors: AuthErrorCopy): string => {
  if (!error || typeof error !== "object" || !("message" in error)) return errors.unexpected;

  // Messages the form already localized pass through unchanged.
  const rawMessage = String((error as { message: string }).message);
  if ([errors.verifyHuman, errors.passwordLeaked, errors.passwordSame].includes(rawMessage)) {
    return rawMessage;
  }

  const message = rawMessage.toLowerCase();

  if (message.includes("invalid login credentials")) {
    return errors.invalidCredentials;
  }

  if (message.includes("email already registered") || message.includes("user already registered")) {
    return errors.emailExists;
  }

  if (message.includes("password should be at least 6 characters")) {
    return errors.passwordTooShort;
  }

  if (message.includes("invalid email")) {
    return errors.invalidEmail;
  }

  if (message.includes("signup is disabled")) {
    return errors.signupDisabled;
  }

  if (message.includes("email rate limit exceeded")) {
    return errors.rateLimited;
  }

  if (message.includes("invalid token") || message.includes("token expired")) {
    return errors.linkExpired;
  }

  // Unmapped errors (network failures like "Failed to fetch", unexpected provider
  // strings, or a JSON parse error from a non-JSON response) must not leak raw,
  // untranslated text into the localized UI.
  return errors.unexpected;
};

type AuthMode = "signin" | "signup" | "forgot";

interface AuthFormProps {
  initialMode?: "signin" | "signup";
  copy?: AuthCopy;
  /**
   * The in-app path to land on after the email link, already checked by the
   * page. Sign-up posts it so the confirmation email carries it; sign-in
   * reads the same value from the URL itself, and switching modes keeps the
   * URL, so one parameter serves both.
   */
  redirectTo?: string;
}

export function AuthForm({ initialMode = "signin", copy = DEFAULT_AUTH_COPY, redirectTo }: AuthFormProps = {}) {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Tracks *why* an error is shown so the invitation-recovery UI can gate on a
  // stable kind rather than substring-matching the localized error text (which
  // never matches in non-English locales).
  const [errorKind, setErrorKind] = useState<"invitation" | null>(null);
  const [message, setMessage] = useState("");
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Bot gate for sign-up: a Turnstile token (required when the site key is
  // configured) and a honeypot field a person never sees.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const turnstileRef = useRef<TurnstileWidgetRef>(null);
  const turnstileRequired = Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY);

  const isSignUp = mode === "signup";
  const isForgot = mode === "forgot";

  // Check for URL parameters on component mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const errorParam = urlParams.get("error");

      if (errorParam === "expired") {
        setError(copy.errors.inviteExpired);
        setErrorKind("invitation");
      } else if (errorParam === "invitation_expired") {
        setError(copy.errors.invitationExpired);
        setErrorKind("invitation");
      } else if (errorParam === "access_denied") {
        setError(copy.errors.accessDenied);
        setErrorKind("invitation");
      }
    }
  }, [copy]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setErrorKind(null);
    setMessage("");

    // Terms validation for sign-up
    if (isSignUp && !termsAccepted) {
      setError(copy.errors.acceptTerms);
      setLoading(false);
      return;
    }

    // Client-side password validation for sign-up
    if (isSignUp) {
      const requirements = getPasswordRequirements(password, copy);
      const unmetRequirements = requirements.filter((req) => !req.met);

      if (unmetRequirements.length > 0) {
        setError(`${copy.errors.passwordRequirementsPrefix} ${unmetRequirements.map((req) => req.label.toLowerCase()).join(", ")}`);
        setLoading(false);
        setShowPasswordRequirements(true);
        return;
      }
    }

    try {
      if (isForgot) {
        // Forgot password: call the server endpoint
        const response = await fetch("/api/auth/forgot-password", {
          method: "POST",
          headers: new Headers({ "Content-Type": "application/json" }),
          body: JSON.stringify({ email }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}) as { error?: string });
          throw new Error(data.error || copy.errors.somethingWentWrong);
        }

        setMessage(copy.resetLinkSent);
      } else if (isSignUp) {
        // Sign up: call the server endpoint (handles existing users with magic link)
        const response = await fetch("/api/auth/signup", {
          method: "POST",
          headers: new Headers({ "Content-Type": "application/json" }),
          body: JSON.stringify({ email, password, honeypot, turnstile_token: turnstileToken, ...(redirectTo ? { redirect_to: redirectTo } : {}) }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}) as { error?: string; code?: string });
          // A used or expired token cannot be sent twice: reset the widget so
          // the next attempt carries a fresh one.
          setTurnstileToken(null);
          turnstileRef.current?.reset();
          if (data.code === "captcha_failed") {
            throw new Error(copy.errors.verifyHuman);
          }
          const rejection = toPasswordRejectionCode(data.code);
          if (rejection) {
            throw new Error(passwordRejectionMessage(rejection, copy.errors));
          }
          throw new Error(data.error || copy.errors.somethingWentWrong);
        }

        setMessage(copy.verificationSent);
      } else {
        // Sign in: direct Supabase call.
        // The @supabase/ssr browser client writes tokens to cookies automatically,
        // so middleware will see the session on the next request. No manual sync needed.
        const { error } = await signIn(email, password);
        if (error) throw error;

        // The middleware sets redirectTo to the page the user originally asked
        // for, path plus query. Only same-origin paths are followed.
        const urlParams = new URLSearchParams(window.location.search);
        const target = parseRedirectTo(urlParams.get("redirectTo"));
        window.location.href = target ? `${target.pathname}${target.search}` : "/a/inbox";
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, copy.errors));
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider: "google" | "azure") => {
    setLoading(true);
    setError("");
    setErrorKind(null);
    try {
      const { error } = await signInWithOAuth(provider);
      if (error) throw error;
    } catch (err: unknown) {
      setError(getErrorMessage(err, copy.errors));
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = getPasswordRequirements(password, copy);
  const passwordScore = passwordRequirements.filter((req) => req.met).length;

  const getTitle = () => {
    if (isForgot) return copy.forgotTitle;
    if (isSignUp) return copy.signUpTitle;
    return copy.signInTitle;
  };

  const getDescription = () => {
    if (isForgot) return copy.forgotDescription;
    if (isSignUp) return copy.signUpDescription;
    return copy.signInDescription;
  };

  const switchMode = (nextMode: AuthMode) => {
    setMode(nextMode);
    setError("");
    setErrorKind(null);
    setMessage("");
  };

  const headerRight = (
    <Button onClick={() => switchMode(isSignUp || isForgot ? "signin" : "signup")} type="button" variant="outline">
      {isSignUp || isForgot ? copy.loginButton : copy.createNewAccountButton}
    </Button>
  );

  return (
    <AuthSplitLayout headerRight={headerRight} painting={isSignUp ? "signUp" : "signIn"} testimonial={copy.testimonial}>
      <div className="flex flex-col gap-section">
        <div className="flex flex-col gap-2">
          <h1 className="text-h1">{getTitle()}</h1>
          <p className="text-body text-muted-foreground">{getDescription()}</p>
        </div>
        <div className="flex flex-col gap-stack">
          {error && (
            <div className="flex items-start gap-2 rounded-card bg-destructive/10 px-inset-lg py-inset text-body text-destructive ring-1 ring-destructive/20">
              <svg aria-hidden className="mt-0.5 size-icon shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  clipRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  fillRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <p>{error}</p>
                {errorKind === "invitation" && (
                  <div className="mt-2 text-meta">
                    <button
                      className="underline"
                      onClick={() => {
                        window.location.href = "/sign-in";
                      }}
                      type="button"
                    >
                      {copy.trySigningIn}
                    </button>
                    {` ${copy.orSeparator} `}
                    <button
                      className="underline"
                      onClick={() => {
                        setError("");
                        setErrorKind(null);
                        setMessage(copy.errors.contactAdminResend);
                      }}
                      type="button"
                    >
                      {copy.requestNewInvitation}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {message && (
            <div className="flex items-start gap-2 rounded-card bg-positive/10 px-inset-lg py-inset text-body text-positive ring-1 ring-positive/20">
              <svg aria-hidden className="mt-0.5 size-icon shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  clipRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  fillRule="evenodd"
                />
              </svg>
              <p>{message}</p>
            </div>
          )}

          {/* OAuth buttons: only shown for signin and signup, hidden after success */}
          {!isForgot && !message && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Button disabled={loading} onClick={() => handleOAuth("google")} type="button" variant="outline">
                  <svg aria-hidden className="size-icon" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  {copy.google}
                </Button>

                <Button disabled={loading} onClick={() => handleOAuth("azure")} type="button" variant="outline">
                  <svg aria-hidden className="size-icon" viewBox="0 0 24 24">
                    <path d="M1 1h10.25v10.25H1z" fill="#f25022" />
                    <path d="M12.75 1H23v10.25H12.75z" fill="#00a4ef" />
                    <path d="M1 12.75h10.25V23H1z" fill="#7fba00" />
                    <path d="M12.75 12.75H23V23H12.75z" fill="#ffb900" />
                  </svg>
                  {copy.microsoft}
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-border border-t" />
                </div>
                <div className="relative flex justify-center text-meta uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{copy.orContinueWith}</span>
                </div>
              </div>
            </>
          )}

          {!message && (
            <form className="flex flex-col gap-stack" onSubmit={handleSubmit}>
              <div className="flex flex-col gap-2">
                <Label className="text-h4" htmlFor="email">
                  {copy.emailLabel}
                </Label>
                <Input
                  className="h-field"
                  disabled={loading}
                  id="email"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={copy.emailPlaceholder}
                  required
                  type="email"
                  value={email}
                />
              </div>

              {/* Password field: hidden in forgot password mode */}
              {!isForgot && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-h4" htmlFor="password">
                      {copy.passwordLabel}
                    </Label>
                    {!isSignUp && (
                      <Button
                        className="h-auto p-0 font-normal text-meta text-muted-foreground hover:text-primary"
                        disabled={loading}
                        onClick={() => switchMode("forgot")}
                        type="button"
                        variant="link"
                      >
                        {copy.forgotPasswordLink}
                      </Button>
                    )}
                  </div>
                  <Input
                    className="h-field"
                    disabled={loading}
                    id="password"
                    minLength={6}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (isSignUp) {
                        setShowPasswordRequirements(true);
                      }
                    }}
                    onFocus={() => isSignUp && setShowPasswordRequirements(true)}
                    placeholder="••••••••••"
                    required
                    type="password"
                    value={password}
                  />

                  {isSignUp && showPasswordRequirements && password && (
                    <PasswordStrength label={copy.passwordStrength} requirements={passwordRequirements} score={passwordScore} />
                  )}
                </div>
              )}

              {isSignUp && (
                <label className="flex items-start gap-2" htmlFor="accept-terms">
                  <input
                    checked={termsAccepted}
                    className="mt-0.5 size-icon shrink-0 cursor-pointer accent-primary"
                    id="accept-terms"
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="text-meta text-muted-foreground">
                    {copy.termsAgree}{" "}
                    <a className="underline hover:text-primary" href="https://www.verbu.com/legal/terms" rel="noopener" target="_blank">
                      {copy.termsOfService}
                    </a>{" "}
                    {copy.termsAnd}{" "}
                    <a className="underline hover:text-primary" href="https://www.verbu.com/legal/privacy" rel="noopener" target="_blank">
                      {copy.privacyPolicy}
                    </a>
                  </span>
                </label>
              )}

              {isSignUp && (
                <>
                  {/* Honeypot: hidden from people, filled by bots */}
                  <input
                    aria-hidden="true"
                    autoComplete="off"
                    className="-left-[9999px] absolute opacity-0"
                    name="company_website"
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    value={honeypot}
                  />
                  <TurnstileWidget onExpire={() => setTurnstileToken(null)} onSuccess={setTurnstileToken} ref={turnstileRef} />
                </>
              )}

              <Button
                className="w-full"
                disabled={loading || (isSignUp && (!termsAccepted || (turnstileRequired && !turnstileToken)))}
                isLoading={loading}
                type="submit"
              >
                {isForgot ? copy.sendResetLinkButton : isSignUp ? copy.createAccountButton : copy.signInButton}
              </Button>
            </form>
          )}

          {/* Mode switching link: mirrors the header's back-and-forth button */}
          {!isForgot && (
            <div className="flex items-center justify-center gap-2 text-body">
              <span className="text-muted-foreground">{isSignUp ? copy.alreadyHaveAccount : copy.dontHaveAccount}</span>
              <Button
                className="h-auto p-0 text-primary hover:underline"
                disabled={loading}
                onClick={() => switchMode(isSignUp ? "signin" : "signup")}
                type="button"
                variant="link"
              >
                {isSignUp ? copy.signInLink : copy.signUpLink}
              </Button>
            </div>
          )}
        </div>
      </div>
    </AuthSplitLayout>
  );
}
